import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  ClipboardCheck,
  CodeXml,
  Fingerprint,
  KeyRound,
  LayoutDashboard,
  PanelsTopLeft,
} from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useMemo, useState } from "react";

import { Account, CurrentSession, MfaStatus, OrganizationDetail, OrganizationSummary, api } from "../api";
import { ErrorState, LoadingState } from "../account/components";
import { StepUpPanel } from "../account/SecurityPage";
import { WorkspaceShell } from "../WorkspaceShell";
import { ApplicationsPage } from "./ApplicationsPage";
import { EventsPage } from "./EventsPage";
import { HwidPage } from "./HwidPage";
import { OidcPage } from "./OidcPage";
import { OverviewPage } from "./OverviewPage";
import { ReviewsPage } from "./ReviewsPage";

const pages = [
  { id: "overview", label: "总览", icon: LayoutDashboard },
  { id: "applications", label: "应用与环境", icon: PanelsTopLeft, permission: "applications.read" },
  { id: "oidc", label: "OIDC", icon: KeyRound, permission: "applications.read" },
  { id: "hwid", label: "原生客户端", icon: Fingerprint, anyPermission: ["devices.read", "devices.manage"] },
  { id: "events", label: "事件", icon: Activity, permission: "audit.read" },
  { id: "reviews", label: "生产审核", icon: ClipboardCheck, permission: "applications.read" },
] as const;

export type DeveloperPageId = (typeof pages)[number]["id"];

function pageFromPath(): DeveloperPageId {
  const value = window.location.pathname.match(/^\/developer(?:\/([^/]+))?$/)?.[1] ?? "overview";
  return pages.some((page) => page.id === value) ? value as DeveloperPageId : "overview";
}

function organizationFromUrl(): string | null {
  return new URLSearchParams(window.location.search).get("organization");
}

export interface DeveloperPageProps {
  organization: OrganizationDetail;
  assurance?: MfaStatus["assurance"];
}

export function DeveloperCenter({
  account,
  session,
  onLoggedOut,
}: {
  account: Account;
  session: CurrentSession;
  onLoggedOut: () => void;
}) {
  const [page, setPage] = useState<DeveloperPageId>(pageFromPath);
  const [organizationId, setOrganizationId] = useState<string | null>(organizationFromUrl);
  const [menuOpen, setMenuOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const organizations = useQuery({
    queryKey: ["organizations"],
    queryFn: () => api<{ organizations: OrganizationSummary[] }>("/api/v1/organizations"),
  });
  const selectedOrganizationId = useMemo(() => {
    const values = organizations.data?.organizations ?? [];
    return values.some((item) => item.id === organizationId) ? organizationId : values[0]?.id ?? null;
  }, [organizationId, organizations.data]);
  const organization = useQuery({
    queryKey: ["organization", selectedOrganizationId],
    queryFn: () => api<OrganizationDetail>(`/api/v1/organizations/${selectedOrganizationId}`),
    enabled: Boolean(selectedOrganizationId),
  });
  const mfa = useQuery({ queryKey: ["mfa"], queryFn: () => api<MfaStatus>("/api/v1/mfa") });
  const logout = useMutation({
    mutationFn: () => api<void>("/api/v1/session", { method: "DELETE" }),
    onSettled: () => { queryClient.clear(); onLoggedOut(); },
  });

  useEffect(() => {
    const changed = () => {
      setPage(pageFromPath());
      setOrganizationId(organizationFromUrl());
    };
    window.addEventListener("popstate", changed);
    return () => window.removeEventListener("popstate", changed);
  }, []);

  useEffect(() => {
    if (selectedOrganizationId && organizationId !== selectedOrganizationId) {
      setOrganizationId(selectedOrganizationId);
      replaceLocation(page, selectedOrganizationId);
    }
  }, [organizationId, page, selectedOrganizationId]);

  const availablePages = pages.filter((item) => {
    const permissions = organization.data?.permissions ?? [];
    if ("permission" in item && item.permission) return permissions.includes(item.permission);
    if ("anyPermission" in item && item.anyPermission) return item.anyPermission.some((value) => permissions.includes(value));
    return true;
  });
  const visiblePage = organization.data && availablePages.some((item) => item.id === page) ? page : "overview";

  useEffect(() => {
    if (organization.data && !availablePages.some((item) => item.id === page)) {
      setPage("overview");
      replaceLocation("overview", selectedOrganizationId);
    }
  }, [availablePages, organization.data, page, selectedOrganizationId]);

  function navigate(next: DeveloperPageId) {
    window.history.pushState(null, "", locationFor(next, selectedOrganizationId));
    setPage(next);
    setMenuOpen(false);
  }

  function selectOrganization(next: string) {
    setOrganizationId(next);
    replaceLocation(page, next);
  }

  if (organizations.isPending) return <main className="bootstrap-state"><LoadingState label="正在打开开发者中心" /></main>;
  if (organizations.error) return <main className="bootstrap-state"><ErrorState error={organizations.error} retry={() => void organizations.refetch()} /></main>;

  return (
    <WorkspaceShell
      variant="developer"
      workspaceLabel="开发者中心"
      workspaceDescription="应用身份、协议与原生设备"
      workspaceIcon={CodeXml}
      navLabel="构建与观测"
      navItems={availablePages}
      activeId={visiblePage}
      onNavigate={navigate}
      account={account}
      statusLabel={mfa.data?.assurance.recent ? `近期验证 AAL${mfa.data.assurance.level}` : "当前为只读模式"}
      statusTone={mfa.data?.assurance.recent ? "good" : "warning"}
      menuOpen={menuOpen}
      setMenuOpen={setMenuOpen}
      loggingOut={logout.isPending}
      onLogout={() => logout.mutate()}
      switcher={(organizations.data?.organizations.length ?? 0) > 0 ? (
        <label className="workspace-switcher">
          <span>当前组织</span>
          <select aria-label="当前组织" value={selectedOrganizationId ?? ""} onChange={(event) => selectOrganization(event.target.value)}>
            {(organizations.data?.organizations ?? []).map((item) => <option key={item.id} value={item.id}>{item.displayName}</option>)}
          </select>
        </label>
      ) : undefined}
    >
        {organizations.data?.organizations.length === 0 && <NoOrganization />}
        {selectedOrganizationId && organization.isPending && <LoadingState label="正在载入组织权限" />}
        {organization.error && <ErrorState error={organization.error} retry={() => void organization.refetch()} />}
        {organization.data && (
          <>
            {!mfa.isPending && !mfa.error && mfa.data && !mfa.data.assurance.recent && (
              <div className="developer-step-up"><StepUpPanel status={mfa.data} onVerified={() => { void mfa.refetch(); void queryClient.invalidateQueries({ queryKey: ["session"] }); }} /></div>
            )}
            {!mfa.isPending && !mfa.error && mfa.data && visiblePage === "oidc" && mfa.data.assurance.recent && mfa.data.assurance.level < 2 && (
              <div className="developer-step-up"><StepUpPanel minimumLevel={2} status={mfa.data} onVerified={() => { void mfa.refetch(); void queryClient.invalidateQueries({ queryKey: ["session"] }); }} /></div>
            )}
            <AnimatePresence mode="wait">
              <motion.div className="developer-stage" key={`${visiblePage}:${organization.data.id}`} initial={reduceMotion ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={reduceMotion ? undefined : { opacity: 0, y: -3 }} transition={{ duration: 0.16 }}>
                {visiblePage === "overview" && <OverviewPage organization={organization.data} session={session} />}
                {visiblePage === "applications" && <ApplicationsPage organization={organization.data} assurance={mfa.data?.assurance} />}
                {visiblePage === "oidc" && <OidcPage organization={organization.data} assurance={mfa.data?.assurance} />}
                {visiblePage === "hwid" && <HwidPage organization={organization.data} assurance={mfa.data?.assurance} />}
                {visiblePage === "events" && <EventsPage organization={organization.data} />}
                {visiblePage === "reviews" && <ReviewsPage organization={organization.data} assurance={mfa.data?.assurance} />}
              </motion.div>
            </AnimatePresence>
          </>
        )}
    </WorkspaceShell>
  );
}

function NoOrganization() {
  return (
    <div className="developer-empty-tenant"><PanelsTopLeft aria-hidden="true" /><h1>还没有开发者组织</h1><p>先在账号中心创建组织并配置成员权限。</p><a className="primary-button" href="/account/organizations">打开组织设置</a></div>
  );
}

function locationFor(page: DeveloperPageId, organizationId: string | null) {
  const path = page === "overview" ? "/developer" : `/developer/${page}`;
  return organizationId ? `${path}?organization=${encodeURIComponent(organizationId)}` : path;
}

function replaceLocation(page: DeveloperPageId, organizationId: string | null) {
  window.history.replaceState(null, "", locationFor(page, organizationId));
}
